import type { Motive } from '@anuleaza/shared';
import { Pill } from '../primitives/Pill.js';
import { MonoLabel } from '../primitives/MonoLabel.js';

interface MotiveCardProps {
  motive: Motive;
}

const PUTERE_TONES: Record<Motive['putere'], { bg: string; color: string; label: string }> = {
  mare: {
    bg: 'oklch(0.94 0.06 145)',
    color: 'oklch(0.42 0.18 145)',
    label: 'NULITATE PUTERNICĂ',
  },
  medie: {
    bg: '#FFF8E5',
    color: '#5C4A0E',
    label: 'ARGUMENT SOLID',
  },
  mică: {
    bg: '#F4F2EB',
    color: '#6B6B66',
    label: 'ARGUMENT SECUNDAR',
  },
};

function formatCitation(m: Motive): string {
  const f = m.fundament_legal;
  let s = `${f.act} art. ${f.articol}`;
  if (f.alineat) s += ` alin. ${f.alineat}`;
  if (f.litera) s += ` lit. ${f.litera})`;
  return s;
}

export function MotiveCard({ motive }: MotiveCardProps) {
  const tone = PUTERE_TONES[motive.putere];
  return (
    <article className="bg-card border border-line rounded-2xl p-6 flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-muted">#{motive.ordine}</span>
          <h3 className="text-lg font-medium text-pretty leading-tight">{motive.titlu}</h3>
        </div>
        <Pill bg={tone.bg} color={tone.color}>
          <span className="font-mono text-[10px] tracking-wider">{tone.label}</span>
        </Pill>
      </header>

      <div className="font-mono text-xs text-accent-dark bg-bg-2 inline-flex w-fit px-3 py-1.5 rounded-md">
        {formatCitation(motive)}
      </div>

      <p className="text-[15px] text-fg-2 leading-relaxed text-pretty">{motive.explicatie}</p>

      {motive.spete_atasate && motive.spete_atasate.length > 0 && (
        <div className="border-t border-line pt-4 mt-1">
          <MonoLabel>Spețe similare</MonoLabel>
          <div className="mt-3 space-y-2.5">
            {motive.spete_atasate.map((s) => {
              const isWon = s.outcome === 'won';
              return (
                <div
                  key={s.rolii_id}
                  className="flex items-start gap-3 text-[13px] leading-relaxed"
                >
                  <Pill
                    bg={isWon ? 'oklch(0.94 0.06 145)' : '#FCE4E6'}
                    color={isWon ? 'oklch(0.42 0.18 145)' : '#A12126'}
                  >
                    <span className="font-mono text-[10px]">
                      {isWon ? 'CÂȘTIGAT' : s.outcome === 'lost' ? 'PIERDUT' : 'PARȚIAL'}
                    </span>
                  </Pill>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-muted truncate">{s.rolii_id}</div>
                    <div className="text-fg-2 text-pretty">{s.rezumat}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </article>
  );
}
